import { buildOptionsFromRecords } from '../../features/filters/simple-filter-editor/BuildSimpleLogicFieldData.js';
import { buildLabelsByStoredValue } from '../../features/filters/simple-filter-editor/SimpleFilterEditorLogic.js';

/** @import { SimpleLogicFieldData } from '../../features/filters/simple-filter-editor/BuildSimpleLogicFieldData.js' */
/** @import { FilterFieldOption } from '../../features/filters/simple-filter-editor/SimpleFilterEditorLogic.js' */

/**
 * Overview fields draw their options from these lookups, since no model attribute picklist holds them.
 * @typedef {Object} OverviewFieldOptionSources
 * @property {FilterFieldOption[]} statusOptions Every distinct workflow status, as collectStatusOptionsFromWorkflows returns it.
 * @property {Object} appTypes Portal app-type configs the model holds, keyed by push id.
 * @property {Object<string, {name: string}>} attachers
 * @property {Object<string, {name: string}>} companies
 */

/**
 * The overview's field data, carrying the labels a key-backed comparison reads alongside the fields themselves.
 * @typedef {SimpleLogicFieldData & {labelsByFieldId: Object<string, Object<string, string>>}} OverviewFieldData
 */

/**
 * Collect the statuses a model's workflows define, along with the workflows declaring each one.
 * @param {Object} appTypes Portal app-type configs the model holds, keyed by push id.
 * @returns {FilterFieldOption[]} Each distinct name once, exactly as the workflow that declared it stores it.
 */
export function collectStatusOptionsFromWorkflows(appTypes) {
  const statusesByStoredName = new Map();
  for (const appType of Object.values(appTypes || {})) {
    const displayName = String(appType?.display_name ?? '');
    const workflowName = displayName.trim() || 'Unknown Application';
    for (const status of Object.values(appType?.statuses || {})) {
      const storedName = String(status?.name ?? '');
      if (!storedName.trim()) continue;

      // A filter compares a job's status to this option exactly, so we key on the name as stored
      if (!statusesByStoredName.has(storedName)) {
        statusesByStoredName.set(storedName, { storedName, workflowNames: new Set() });
      }
      const collectedStatus = statusesByStoredName.get(storedName);
      collectedStatus.workflowNames.add(workflowName);
    }
  }

  const collectedStatuses = Array.from(statusesByStoredName.values());
  // The label is trimmed for reading while the value keeps the spacing a job stores, so a comparison still matches
  return collectedStatuses.map(({ storedName, workflowNames }) => ({
    label: storedName.trim(),
    value: storedName,
    description: [...workflowNames].join(', ')
  }));
}

/**
 * Give the overview's fields the options their stored values allow, and the labels those values stand for.
 * @param {SimpleLogicFieldData} fieldData
 * @param {OverviewFieldOptionSources} optionSources
 * @returns {OverviewFieldData} Every path unchanged, with each lookup-backed field offering the options it can show.
 */
export function applyOverviewFieldOptions(fieldData, optionSources) {
  const sources = optionSources || {};
  const dataPathsById = fieldData?.dataPathsById || {};
  // A job stores keys these columns never show, so each of these fields offers the names its lookup holds
  const keyBackedOptions = {
    'metadata.app_type': buildOptionsFromRecords(sources.appTypes, 'display_name'),
    'metadata.attachment_owner': buildOptionsFromRecords(sources.attachers),
    'metadata.creator': buildOptionsFromRecords(sources.companies)
  };

  /** @type {Object<string, Object<string, string>>} */
  const labelsByFieldId = {};
  const fields = (fieldData?.fields || []).map((field) => {
    const dataPath = dataPathsById[field.id];
    if (dataPath === 'metadata.app_status') return { ...field, options: sources.statusOptions || [] };
    if (!(dataPath in keyBackedOptions)) return field;

    const options = keyBackedOptions[dataPath];
    // A job storing a retired key stays reachable, since presence is all an empty lookup can still test
    if (options.length === 0) return { ...field, options, operators: ['exists', 'does_not_exist'] };

    // The dropdown and the comparison must never disagree, so we build the labels beside the options
    labelsByFieldId[field.id] = buildLabelsByStoredValue(options);
    return { ...field, options };
  });
  return { ...fieldData, fields, dataPathsById, labelsByFieldId };
}
